import { S25Const } from "../s25-const";
import { jSith } from "../jquery-replacement";
import { S25Datefilter } from "../../modules/s25-dateformat/s25.datefilter.service";
import { S25DateParse } from "./s25.date.parse";
import { S25Dateformat } from "../../modules/s25-dateformat/s25.dateformat.service";
import type { EventSummary } from "../../modules/s25-swarm-schedule/s25.event.summary.service";
import { Proto } from "../../pojo/Proto";
import { S25Array } from "./s25.array";
import { S25Duration } from "./s25.duration";
import { S25String } from "./s25.string";
import { S25Number } from "./s25.number";
import { isArray } from "./index";
import ISODateString = Proto.ISODateString;
import ISOTimeString = Proto.ISOTimeString;
import DowName = Proto.DowName;
import DowNumber = Proto.DowNumber;
import { MemoRepository } from "../../decorators/memo.decorator";

export namespace S25Date {
    export function current(): Date {
        return new Date();
    }

    export function currentDate(): Date {
        return getDate(current());
    }

    export function currentDateTime(): Date {
        const dateTime = current();
        dateTime.setSeconds(0);
        dateTime.setMilliseconds(0);
        return dateTime;
    }

    export const dowInt = {
        SU: 0,
        MO: 1,
        TU: 2,
        WE: 3,
        TH: 4,
        FR: 5,
        SA: 6,
        U: 0,
        M: 1,
        T: 2,
        W: 3,
        R: 4,
        F: 5,
        S: 6,
    } as Record<string, 0 | 1 | 2 | 3 | 4 | 5 | 6>;

    export const dowIntToChar = {
        0: "U",
        1: "M",
        2: "T",
        3: "W",
        4: "R",
        5: "F",
        6: "S",
    } as const;

    export function dowSort(a: any, b: any): -1 | 0 | 1 {
        if (dowInt[a] > dowInt[b]) {
            return 1;
        } else if (dowInt[a] < dowInt[b]) {
            return -1;
        } else {
            return 0;
        }
    }

    export function toDow(date: Date, dow: string, direction: "sameWeek" | "backward" | "forward"): Date {
        const targetDow = jSith.coalesce(dowInt[dow], date.getDay());
        let diff = targetDow - date.getDay();
        if (direction === "forward" && diff < 0) {
            diff += 7;
        } else if (direction === "backward" && diff > 0) {
            diff -= 7;
        }
        return addDays(date, diff);
    }

    export function createDate(year: any, month: any, day: any): Date {
        return new Date(S25Number.parseInt(year), S25Number.parseInt(month) - 1, S25Number.parseInt(day));
    }

    export function hourMinuteString(date: any): string {
        if (date && date.getHours) {
            return S25String.leftPad(date.getHours(), 2) + ":" + S25String.leftPad(date.getMinutes(), 2) + ":00";
        } else {
            if (typeof date === "string" && /^\d\d:\d\d$/.test(date)) {
                date += ":00";
            }
            return date;
        }
    }

    export function isBetween(date: any, date1: any, date2: any): boolean {
        date = S25DateParse(date);
        return date > S25DateParse(date1) && date < S25DateParse(date2);
    }

    export function isBetweenEqual(date: any, date1: any, date2: any): boolean {
        date = S25DateParse(date);
        return date >= S25DateParse(date1) && date <= S25DateParse(date2);
    }

    /**
     * Checks if two date ranges overlap (have any time in common).
     * Uses the standard interval overlap algorithm: start1 <= end2 && start2 <= end1
     *
     * @param start1 Start of first date range
     * @param end1 End of first date range
     * @param start2 Start of second date range
     * @param end2 End of second date range
     * @returns true if the ranges overlap, false otherwise
     */
    export function doRangesOverlap(
        start1: Date | string | number,
        end1: Date | string | number,
        start2: Date | string | number,
        end2: Date | string | number,
    ): boolean {
        const s1 = S25DateParse(start1);
        const e1 = S25DateParse(end1);
        const s2 = S25DateParse(start2);
        const e2 = S25DateParse(end2);
        return s1 <= e2 && s2 <= e1;
    }

    export function addYears(date: any, years: number): Date {
        return _add(date, years, "year");
    }

    export function addMonths(date: any, months: number): Date {
        return _add(date, months, "month");
    }

    export function addWeeks(date: any, weeks: number): Date {
        return _add(date, weeks, "week");
    }

    export function addDays(date: any, days: number): Date {
        return _add(date, days, "day");
    }

    export function addHours(date: any, hours: number): Date {
        return _add(date, hours, "hour");
    }

    export function addMinutes(date: any, minutes: number): Date {
        return _add(date, minutes, "minute");
    }

    export function addSeconds(date: any, seconds: number): Date {
        return _add(date, seconds, "second");
    }

    export const addHoursToS25ISODateTimeStr = MemoRepository.memoize(
        (date: ISODateString, hours: number): ISODateString => {
            return toS25ISODateTimeStrFromDate(addHours(date, hours));
        },
    );

    export function toEndOfDay(date: any): Date {
        if (!date) {
            return null;
        }
        const clone = S25DateParse(date);
        clone.setHours(23, 59, 59, 0);
        return clone;
    }

    export function toStartOfDay(date: any): Date {
        if (!date) {
            return null;
        }
        const clone = S25DateParse(date);
        clone.setHours(0, 0, 0, 0);
        return clone;
    }

    export function toStartOfHour(date: any): Date {
        if (!date) {
            return null;
        }
        const clone = S25DateParse(date);
        clone.setMinutes(0, 0, 0);
        return clone;
    }

    export function _add(date: any, value: number, durationName: string): Date {
        let d = window.dayjs(date);
        if (value && durationName) {
            d = d.add(S25Number.toFloat(value), durationName);
        }
        return d.toDate();
    }

    export function copyHourMinute(date: any, hours: Date): Date {
        return window
            .dayjs(date)
            .startOf("minute")
            .set("hour", hours.getHours())
            .set("minute", hours.getMinutes())
            .toDate();
    }

    export function dropTZString(dateStr: any): string {
        return toS25ISODateTimeStr(S25DateParse.parseDropTZ(dateStr), true);
    }

    export function dropTZFromISODateTimeString(date: ISODateString) {
        // Only use with ISO date strings starting with yyyy-mm-ddThh:mm:ss
        return date.slice(0, 19);
    }

    export const nextUnit = {
        second: "minute",
        minute: "hour",
        hour: "day",
        day: "month",
        month: "year",
    } as const;

    export function equalTime(
        date1: string | Date,
        date2: string | Date,
        unit?: "second" | "minute" | "hour" | "day" | "month",
    ): boolean {
        unit = unit || "second";
        const coarserUnit = nextUnit[unit];
        return (
            window.dayjs(S25DateParse.time(date1)).isSame(S25DateParse.time(date2), unit) &&
            (!coarserUnit || equalTime(date1, date2, coarserUnit as "second" | "minute" | "hour" | "day" | "month"))
        );
    }

    export function isValidTime(time: any): boolean {
        if (!time) {
            return false;
        }
        return isValid(S25DateParse.time(time));
    }

    export function getDate(date: any): Date {
        const clone = S25DateParse(date);
        clone.setHours(0, 0, 0, 0);
        return clone;
    }

    export function isValid(date: any, allowZeroTime?: boolean): boolean {
        if (!date) {
            return false;
        } else if (isArray(date)) {
            return false;
        }
        date = jSith.isParsableAsInt(date) ? jSith.toStr(date) : date;
        date = S25DateParse(date);
        return !(
            !date ||
            !date.toString ||
            date.toString() === "Invalid Date" ||
            !date.getTime ||
            isNaN(date.getTime()) ||
            (date.getTime() <= 0 && !allowZeroTime)
        );
    }

    export function toTimeStr(date: any, is24?: boolean): string {
        if (!date) {
            return null;
        }
        date = S25DateParse(date);
        if (!isValid(date, true)) {
            return null;
        }

        return toTimeStrFromHours(date.getHours() + date.getMinutes() / 60, is24);
    }

    export function ariaTimeString(timeStr: string): string {
        return timeStr.replace(/\s|0(?=\d:)|:00/g, "").toUpperCase();
    }

    export function toTimeStrFromHours(hours: number, is24?: boolean): string {
        if (is24) {
            return toS25ISOTimeStrFromHours(hours).slice(0, 5);
        }

        hours = ((hours % 24) + 24) % 24; // Make sure hours are [0, 24)
        const minutes = Math.round(hours * 60); // Convert to minutes so that mm !== 60
        hours = Math.floor(minutes / 60) % 24;

        const amPm = hours >= 12 ? "pm" : "am";
        if (hours === 0) hours += 12;
        else if (hours >= 13) hours -= 12;

        const hh = String(hours).padStart(2, "0");
        const mm = String(minutes % 60).padStart(2, "0");
        return `${hh}:${mm} ${amPm}`;
    }

    export function toS25ISODateTimeStrFromHours(hours: number) {
        return toS25ISODateTimeStr(toTimeStrFromHours(hours)!)!;
    }

    export function toS25ISOTimeStr(date: any): string {
        if (!date) {
            return null;
        }
        date = S25DateParse(date);
        if (!isValid(date, true)) {
            return null;
        }
        const dateTimeStr = toS25ISODateTimeStr(date, false); //time str does not need seconds, just hours/minutes
        return dateTimeStr.substring(dateTimeStr.indexOf("T") + 1);
    }

    export function toS25ISOTimeStrFromDate(date: Date, keepSeconds: boolean = false): ISOTimeString {
        if (!date) return null;

        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = keepSeconds ? String(date.getSeconds()).padStart(2, "0") : "00";

        return `${hours}:${minutes}:${seconds}`;
    }

    export function toS25ISOTimeStrFromHours(hours: number): string {
        hours = ((hours % 24) + 24) % 24; // Make sure hours are [0, 24)
        const minutes = Math.round(hours * 60); // Convert to minutes so that mm !== 60
        const hh = String(Math.floor((minutes / 60) % 24)).padStart(2, "0"); // Mod24 -> hours !== 24
        const mm = String(Math.round(minutes % 60)).padStart(2, "0");
        return `${hh}:${mm}:00`;
    }

    export function toS25ISODateStr(date: number | string | Date | undefined): string | undefined {
        const dateString = toS25ISODateTimeStr(date);
        if (!dateString) {
            return dateString;
        }
        return dateString.substring(0, dateString.indexOf("T"));
    }

    /**
     * Civil YYYY-MM-DD for date-only API fields and pickers (avoids UTC shift when the value already
     * begins with an ISO date prefix).
     */
    export function toCivilDateString(val: string | Date | undefined | null): ISODateString | undefined {
        if (val == null || val === "") {
            return undefined;
        }
        if (typeof val === "string") {
            const trimmed = val.trim();
            if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
                return trimmed.substring(0, 10) as ISODateString;
            }
            return toS25ISODateStr(trimmed) as ISODateString | undefined;
        }
        return toS25ISODateStr(val) as ISODateString | undefined;
    }

    export function toS25ISODateTimeStr(
        date: number | string | Date | undefined,
        keepSeconds?: boolean,
    ): string | undefined {
        if (!date) {
            return undefined;
        }
        date = S25DateParse(date);
        if (!isValid(date, true)) {
            return undefined;
        }
        return toS25ISODateTimeStrFromDate(date, keepSeconds);
    }

    export function toS25ISODateTimeStrFromDate(date: Date, keepSeconds?: boolean): ISODateString {
        if (!date || isNaN(date.getTime?.())) return;
        !keepSeconds && date.setSeconds(0); //no need for seconds in any place in 25live
        return S25Datefilter.transform(S25DateParse.parseDropTZ(date), S25Dateformat.getISODateTimeFormat());
    }

    export function toS25ISODateTimeStrNoPunc(date: any): string {
        return toS25ISODateTimeStr(date).replace(/[-:]/g, "");
    }

    export function toS25ISODateTimeStrNoPuncEndOfDay(date: any): string {
        return toS25ISODateStrEndOfDay(date).replace(/[-:]/g, "");
    }

    export function toS25ISODateStrStartOfDay(date: any): string {
        return date && toS25ISODateStr(date) + "T00:00:00";
    }

    export function toS25ISODateStrEndOfDay(date: any): string {
        return toS25ISODateTimeStr(toEndOfDay(date), true);
    }

    export function toDateTimeNumeric(date: any): string {
        return (toS25ISODateTimeStr(date) || "").replace(/[\-:]/g, "");
    }

    export function equal(date1: any, date2: any): boolean {
        return date1 && date2 && window.dayjs(date1).isSame(date2);
    }

    export function equalMonth(date1: any, date2: any): boolean {
        //ignore time in date-only comparison
        return date1 && date2 && window.dayjs(date1).isSame(date2, "month");
    }

    export function equalYear(date1: any, date2: any): boolean {
        return date1 && date2 && window.dayjs(date1).isSame(date2, "year");
    }

    export function equalDate(date1: any, date2: any): boolean {
        return date1 && date2 && window.dayjs(S25DateParse(date1)).isSame(S25DateParse(date2), "day");
    }

    export function equalDates(date1: Date | undefined, date2: Date | undefined): boolean {
        return window.dayjs(date1).isSame(date2);
    }

    export function getDayOfWeek(date: any): number {
        //return day of week (dow)
        return S25DateParse(date).getDay();
    }

    export function dateToHours(date: any): number {
        //return hours and fractional hours (min/60) of a date
        date = S25DateParse(date);
        return date.getHours() + date.getMinutes() / 60;
    }

    export function timeToHours(time: ISOTimeString): number {
        const [h, m, s] = time.split(":").map(Number);
        return h + (m || 0) / 60 + (s || 0) / 3600;
    }

    export function copy(dst: Date, src: Date): Date {
        copyYearMonthDay(dst, src);
        copyTime(dst, src);
        return dst;
    }

    export function copyYearMonthDay(dst: Date, src: Date): Date {
        dst.setFullYear(src.getFullYear());
        dst.setMonth(src.getMonth());
        dst.setDate(src.getDate());
        return dst;
    }

    export function copyTime(dst: Date, src: Date): Date {
        dst.setHours(src.getHours());
        dst.setMinutes(src.getMinutes());
        dst.setSeconds(src.getSeconds());
        dst.setMilliseconds(src.getMilliseconds());
        return dst;
    }

    export function printDay(d: number, forceDay?: boolean): string {
        return d || forceDay ? "(" + (d > 0 ? "+" : "") + d + "d) " : "";
    }

    export function printTime(time: any, timeFormat: string): string {
        const d: number = time.day,
            h: number = time.hour,
            m: number = time.minute;
        const formattedTime: string = S25Datefilter.transform(S25DateParse.time(h + ":" + m), timeFormat);
        return printDay(d) + formattedTime;
    }

    export function printMinutes(mins: any, timeFormat: string): string {
        return printTime(S25Duration.minutesToTime(mins), timeFormat);
    }

    export function printDurationMinutes(mins: any): string {
        const time: any = S25Duration.minutesToTime(mins),
            d: number = time.day,
            h: number = time.hour,
            m: number = time.minute;
        return printDuration(d, h, m);
    }

    export function printDuration(days: number, hours: number, minutes: number) {
        const d = days ? `${days}d ` : "";
        const h = hours ? `${hours}h ` : "";
        const m = minutes || (!days && !hours) ? `${minutes}m` : "";
        return d + h + m;
    }

    //https://stackoverflow.com/questions/2536379/difference-in-months-between-two-dates-in-javascript
    //note these are actually partial months counted as full (used in s25-profile-util)
    export function diffMonths(start: any, end: any): number {
        start = S25DateParse(start); // clone
        end = S25DateParse(end); // clone
        let months: number = (end.getFullYear() - start.getFullYear()) * 12;
        months += end.getMonth() - start.getMonth();
        return months;
    }

    // note: this function purposely drops any TIME from Date object, and compares Days only;
    // i.e. it returns one Day difference for (yesterday 23:59:00, today 00:00:00) - note this is the desired behaviour!
    // correct day diff, ignoring DST where millisecondsInDay !== 86400000
    // 2070117, Mikhail - tzName is required to get userpreference timezone instead of browser local timezone
    // because dates (start, end) have browser local timezone
    // http://stackoverflow.com/questions/3224834/get-difference-between-2-dates-in-javascript
    export function diffDays(start: number | string | Date, end: number | string | Date): number {
        const millisecondsInDay = 86400000; // 1000*60*60*24
        start = S25DateParse(start); // clone
        end = S25DateParse(end); // clone
        // Discard the time and time-zone information.
        const startUtc = Date.UTC(
            start.getFullYear(),
            start.getMonth(),
            start.getDate(),
            start.getMinutes(),
            start.getSeconds(),
        );
        const endUtc = Date.UTC(
            end.getFullYear(),
            end.getMonth(),
            end.getDate(),
            start.getMinutes(),
            start.getSeconds(),
        );
        return Math.ceil((endUtc - startUtc) / millisecondsInDay);
    }

    export function diffWeeks(start: any, end: any, weekstart?: number): number {
        return (diffDays(firstDayOfWeek(start, weekstart), lastDayOfWeek(end, weekstart)) + 1) / 7;
    }

    export function diffHours(startDt: any, endDt: any): number {
        return diffMinutes(startDt, endDt) / 60;
    }

    export function diffMinutes(startDt: any, endDt: any): number {
        return S25Number.toInt((S25DateParse(endDt).getTime() - S25DateParse(startDt).getTime()) / S25Const.ms.min);
    }

    export function firstDayOfMonth(date: any): Date {
        const newDate: Date = S25DateParse(date); //ensure input is date
        return new Date(newDate.getFullYear(), newDate.getMonth(), 1); //year, month, day 1
    }

    export function lastDayOfMonth(date: any): Date {
        return addDays(addMonths(firstDayOfMonth(date), 1), -1);
    }

    export function firstDayOfWeek(date: number | string | Date, weekstart?: number): Date {
        //weekstart: 0 is Sunday
        weekstart = weekstart || 0;
        const newDate: Date = S25DateParse(date); //ensure input is date
        return firstDayOfWeekFromDate(newDate, weekstart);
    }

    export function firstDayOfWeekFromDate(date: Date, weekStart: number) {
        // Subtract weekstart to get the distance from user's start of week, add 7 to wrap around if it's
        // negative (7 more "days" is the same day of week), use mod to compress to just 0-6.
        // Example: today is Weds, weekStart is Thurs: 3 - 4 = -1, +7 = 6, mod 7 = 6.
        // Subtract 6 days from today (Weds) and you get the former Thurs.
        const offsetDays = (date.getDay() - weekStart + 7) % 7;
        return addDays(date, -offsetDays);
    }

    export function lastDayOfWeek(date: number | string | Date, weekstart?: number): Date {
        return addDays(firstDayOfWeek(date, weekstart), 6);
    }

    export function dayOfWeekNumber(dow: DowName): DowNumber {
        const dows = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        return dows.indexOf(dow) as DowNumber;
    }

    export function toDayOfWeek(date: Date, dow: DowNumber): Date {
        const diff = dow - date.getDay();
        return addDays(date, diff);
    }

    export function getDaysInMonth(month: number, year: number): number {
        return new Date(year, month, 0).getDate();
    }

    export function syncDateToDate(date1: Date, date2: Date): Date {
        date1.setDate(1); //set date first to a known OK value as when we change year/month, the day might not be valid (eg, 31 in november)
        date1.setSeconds(0);
        date1.setMilliseconds(0);
        date1.setFullYear(date2.getFullYear());
        date1.setMonth(date2.getMonth());
        date1.setDate(date2.getDate());
        return date1;
    }

    export function syncDateToTime(date1: Date, date2: Date): Date {
        date1.setSeconds(0);
        date1.setMilliseconds(0);
        date1.setHours(date2.getHours());
        date1.setMinutes(date2.getMinutes());
        return date1;
    }

    export function syncDateAll(date1: Date, date2: Date): Date {
        syncDateToDate(date1, date2);
        syncDateToTime(date1, date2);
        return date1;
    }

    export function setS25ISODateTimeStrToHours(dateStr: ISODateString, hours: number): ISODateString {
        const date = S25DateParse(dateStr);
        setHours(date, hours);
        return toS25ISODateTimeStrFromDate(date);
    }

    export function setHours(date: Date, hours: number): Date {
        date.setHours(Math.floor(hours));
        date.setMinutes((hours % 1) * 60);
        return date;
    }

    export function fromToString(date: any, endDate: any, dateFormat: string): string {
        if (!endDate || equalDate(date, endDate)) {
            return S25Datefilter.transform(date, dateFormat);
        }
        return S25Datefilter.transform(date, dateFormat) + " - " + S25Datefilter.transform(endDate, dateFormat);
    }

    export function getDateTimeSpan(
        start: Date,
        end: Date,
        options: { dateTimeFormat: string; timeFormat: string },
    ): string {
        if (equalDate(start, end)) {
            const startTime = S25Datefilter.transform(start, options.timeFormat);
            const endTime = S25Datefilter.transform(end, options.timeFormat);
            return `${startTime} - ${endTime}`;
        }
        const startDateTime = S25Datefilter.transform(start, options.dateTimeFormat);
        const endDateTime = S25Datefilter.transform(end, options.dateTimeFormat);
        return `${startDateTime} - ${endDateTime}`;
    }

    export function suffixDay(day: Parameters<typeof S25Number.lastDigit>[0]): string {
        const number = Math.abs(Number(day) % 100);
        if (number >= 11 && number <= 13) return day + "th"; // x11th, x12th, x13th
        const lastDigit = S25Number.lastDigit(day);
        return day + (lastDigit === 1 ? "st" : lastDigit === 2 ? "nd" : lastDigit === 3 ? "rd" : "th");
    }

    export function getDayName(dow: number) {
        const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;
        return dayNames[dow % dayNames.length]; //circular array, so 0-6, then 7 is 0, 8 is 1, and so on
    }

    /**
     * @param weekStart Sunday=0, Monday=1
     * @returns {string[]} Array of full weekday names, capitalized
     */
    export function getDayNames(weekStart: number) {
        const dowArr = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        return S25Array.rotate(dowArr, -weekStart);
    }

    export function getMonthName(date: any): string {
        date = S25DateParse(date); //ensure input is date
        const monthNames: string[] = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];
        return monthNames[date.getMonth()];
    }

    export function toYMD(date?: Date): { year?: number; month?: number; day?: number } {
        return date ? { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() } : {};
    }

    export function changeDow(date: Date, dow: number): Date {
        if (!date) return null;
        return addDays(date, dow - date.getDay());
    }

    export function changeDowS25ISOString(date: ISODateString, dow: number): ISODateString {
        const newDate = changeDow(S25DateParse(date), dow);
        return toS25ISODateTimeStrFromDate(newDate);
    }

    export function getGMT(date: Date, colon?: boolean): string {
        let gmt = new Date(date).toString().match(/([-\+][0-9]+)\s/)[1];
        colon ? (gmt = gmt.substring(0, 3) + ":" + gmt.substring(3, 5)) : "";
        return gmt;
    }

    /**
     * Creates an object with the DOWs spanned by the start/end dates
     * @param start
     * @param end
     * @param dows
     * @returns dows An object with DOW keys and value true
     */
    export function getDows(
        start: Date,
        end: Date,
        dows: Set<EventSummary.DowChar> = new Set(),
    ): Set<EventSummary.DowChar> {
        // Iterate from start to end and find all dows
        while (start < end) {
            const dow = dowIntToChar[start.getDay() as keyof typeof dowIntToChar];
            dows.add(dow);
            start = addHours(start, 20); // Don't add a full day to circumvent any DST issues
        }
        const dow = dowIntToChar[end.getDay() as keyof typeof dowIntToChar];
        dows.add(dow);
        return dows;
    }

    export function isToday(date: Date): boolean {
        const today = new Date();
        if (date.getDate() !== today.getDate()) return false;
        if (date.getMonth() !== today.getMonth()) return false;
        if (date.getFullYear() !== today.getFullYear()) return false;
        return true;
    }

    export function isBefore(date: number | string | Date): boolean {
        return S25DateParse(date) > new Date();
    }

    export function isAfter(date: number | string | Date): boolean {
        return S25DateParse(date) < new Date();
    }
}
